import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Mail,
  Phone,
  Instagram,
  Calendar,
  Trophy,
  Users,
  Flame,
  ArrowRight,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import Countdown from "@/components/Countdown";

const HERO_IMG =
  "https://images.unsplash.com/photo-1756976542740-0633c1493261?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxzdHJlZXRiYWxsJTIwYWN0aW9uJTIwZGFya3xlbnwwfHx8fDE3NzY4MDQ4NTR8MA&ixlib=rb-4.1.0&q=85";

const FORMAT = [
  {
    day: "Giorno 01 — 02",
    title: "Fase a gironi",
    caption: "Due giornate",
    description:
      "Le squadre si affrontano nei gironi. Ogni canestro conta, ogni possesso pesa. Si comincia a scremare il tabellone.",
    icon: Users,
    tone: "lg:col-span-5",
  },
  {
    day: "Giorno 03",
    title: "Gironi a incroci",
    caption: "Una giornata",
    description:
      "Il caos organizzato: le migliori si incrociano, nessuno può permettersi il lusso di un passo falso.",
    icon: Flame,
    tone: "lg:col-span-4",
  },
  {
    day: "Giorno 04",
    title: "Le Finali",
    caption: "Atto finale",
    description:
      "Semifinali, finali e consacrazione. Una sera, una metà campo, un solo trono: chi infrange le regole vince.",
    icon: Trophy,
    tone: "lg:col-span-3",
  },
];

const INFO = [
  {
    label: "Quando",
    value: "2—5 Luglio 2026",
    sub: "4 giorni di pura adrenalina",
    icon: Calendar,
  },
  {
    label: "Dove",
    value: "Parco della Libertà",
    sub: "Viale dei Kennedy — Maserà di Padova (PD)",
    icon: MapPin,
  },
  {
    label: "Formato",
    value: "3x3 Streetball",
    sub: "Gironi, incroci e finali su asfalto",
    icon: Trophy,
  },
];

export default function Home() {
  return (
    <div data-testid="page-home" className="text-white">
      <section
        data-testid="hero"
        className="relative min-h-[100vh] w-full overflow-hidden flex items-end"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Streetball action"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/60 to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24 pt-36">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#CCFF00] font-bold mb-6"
          >
            <span className="w-8 h-[2px] bg-[#CCFF00]" />
            3x3 Streetball Tournament — Edizione 2026
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-bebas uppercase leading-[0.85] text-white"
          >
            <span className="block text-6xl sm:text-7xl md:text-[9vw]">
              Break the
            </span>
            <span className="block text-6xl sm:text-7xl md:text-[9vw] text-[#FF4500]">
              Rules <span className="text-white">3x3</span>
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:gap-12"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3] font-bold">
                Quattro giorni · Un solo campione
              </p>
              <p className="mt-3 text-lg md:text-xl text-white/90 max-w-xl leading-relaxed">
                Dal <span className="text-[#FF4500] font-bold">2 al 5 Luglio 2026</span>,
                il Parco della Libertà di Maserà si trasforma nella capitale del
                3x3. Playground, musica, competizione vera.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 md:ml-auto">
              <Link
                to="/iscrizioni"
                data-testid="hero-cta-iscriviti"
                className="inline-flex items-center gap-3 bg-[#FF4500] text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-[#FF4500] hover:bg-transparent hover:text-[#FF4500] transition-colors duration-300"
              >
                Iscrivi il tuo team
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/galleria"
                data-testid="hero-cta-galleria"
                className="inline-flex items-center gap-3 bg-transparent text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-white hover:bg-white hover:text-black transition-colors duration-300"
              >
                Vivi l'atmosfera
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-[#2A2A2A] bg-[#050505]/70 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-xs uppercase tracking-[0.2em] font-bold">
            <div>
              <span className="text-[#A3A3A3]">Edizione</span>
              <p className="text-white mt-1">2026</p>
            </div>
            <div>
              <span className="text-[#A3A3A3]">Giorni</span>
              <p className="text-white mt-1">04</p>
            </div>
            <div>
              <span className="text-[#A3A3A3]">Formato</span>
              <p className="text-white mt-1">3x3</p>
            </div>
            <div>
              <span className="text-[#A3A3A3]">Ingresso</span>
              <p className="text-[#CCFF00] mt-1">20€/GIOCATORE</p>
            </div>
          </div>
        </div>
      </section>

      <Marquee text="Break the Rules" />

      <Countdown target="2026-07-02T10:00:00+02:00" />

      <section
        data-testid="quick-info"
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {INFO.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative bg-[#0A0A0A] border border-[#2A2A2A] p-8 hover:border-[#FF4500] transition-colors duration-300"
                data-testid={`info-card-${item.label.toLowerCase()}`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">
                    {item.label}
                  </span>
                  <Icon size={22} className="text-[#FF4500]" strokeWidth={2} />
                </div>
                <p className="mt-8 font-bebas text-5xl uppercase leading-none text-white group-hover:text-[#FF4500] transition-colors">
                  {item.value}
                </p>
                <p className="mt-4 text-sm text-[#A3A3A3] leading-relaxed">
                  {item.sub}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section
        data-testid="format-section"
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] font-bold">
              / 01 · Il Torneo
            </p>
            <h2 className="mt-4 font-bebas text-5xl md:text-7xl uppercase leading-[0.9]">
              Quattro giorni <br />
              <span className="text-[#FF4500]">di battaglie</span> nel playground
            </h2>
          </div>
          <p className="max-w-md text-[#A3A3A3] leading-relaxed">
            Un format pensato per spremere ogni team fino all'ultimo possesso.
            Si parte dai gironi, si passa per gli incroci, si chiude con una
            notte di finali da brividi.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {FORMAT.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.article
                key={f.day}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`${f.tone} group relative bg-[#0A0A0A] border border-[#2A2A2A] p-8 md:p-10 hover:border-[#FF4500] transition-colors duration-300`}
                data-testid={`format-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#CCFF00] font-bold">
                    {f.day}
                  </span>
                  <Icon
                    size={22}
                    className="text-[#FF4500] group-hover:rotate-[8deg] transition-transform"
                  />
                </div>
                <h3 className="mt-10 font-bebas text-4xl md:text-5xl uppercase leading-none text-white">
                  {f.title}
                </h3>
                <p className="mt-2 font-bebas text-xl text-[#FF4500] uppercase tracking-wider">
                  {f.caption}
                </p>
                <p className="mt-6 text-[#A3A3A3] leading-relaxed">
                  {f.description}
                </p>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section
        data-testid="contact-section"
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-[#0A0A0A] border border-[#2A2A2A] p-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] font-bold">
              / 02 · Contatti
            </p>
            <h2 className="mt-4 font-bebas text-5xl md:text-6xl uppercase leading-[0.9]">
              Scrivici. <br />
              <span className="text-[#FF4500]">Chiamaci.</span>{" "}
              Seguici.
            </h2>

            <ul className="mt-10 space-y-6">
              <li>
                <a
                  href="mailto:infobtr3x3@gmail.com"
                  data-testid="contact-email"
                  className="group flex items-center gap-4 border-b border-[#2A2A2A] pb-5 hover:border-[#FF4500] transition-colors"
                >
                  <span className="w-12 h-12 border border-[#2A2A2A] flex items-center justify-center group-hover:bg-[#FF4500] group-hover:border-[#FF4500] transition-colors shrink-0">
                    <Mail size={18} />
                  </span>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">
                      Email
                    </p>
                    <p className="text-lg md:text-xl text-white group-hover:text-[#FF4500] transition-colors break-all">
                      infobtr3x3@gmail.com
                    </p>
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-[#A3A3A3] group-hover:text-[#FF4500] group-hover:translate-x-1 transition-all"
                  />
                </a>
              </li>
              <li>
                <a
                  href="tel:+393295653110"
                  data-testid="contact-phone"
                  className="group flex items-center gap-4 border-b border-[#2A2A2A] pb-5 hover:border-[#FF4500] transition-colors"
                >
                  <span className="w-12 h-12 border border-[#2A2A2A] flex items-center justify-center group-hover:bg-[#FF4500] group-hover:border-[#FF4500] transition-colors shrink-0">
                    <Phone size={18} />
                  </span>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">
                      Telefono
                    </p>
                    <p className="text-lg md:text-xl text-white group-hover:text-[#FF4500] transition-colors">
                      329 5653110
                    </p>
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-[#A3A3A3] group-hover:text-[#FF4500] group-hover:translate-x-1 transition-all"
                  />
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com/break_the_rules_trnmnt"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-instagram"
                  className="group flex items-center gap-4 pb-5 hover:border-[#FF4500] transition-colors"
                >
                  <span className="w-12 h-12 border border-[#2A2A2A] flex items-center justify-center group-hover:bg-[#FF4500] group-hover:border-[#FF4500] transition-colors shrink-0">
                    <Instagram size={18} />
                  </span>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">
                      Instagram
                    </p>
                    <p className="text-lg md:text-xl text-white group-hover:text-[#FF4500] transition-colors">
                      @break_the_rules_trnmnt
                    </p>
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-[#A3A3A3] group-hover:text-[#FF4500] group-hover:translate-x-1 transition-all"
                  />
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7 relative bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden">
            <div className="p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] font-bold">
                / 03 · Dove si gioca
              </p>
              <h2 className="mt-4 font-bebas text-5xl md:text-6xl uppercase leading-[0.9]">
                Parco della <br />
                <span className="text-[#FF4500]">Libertà</span>
              </h2>
              <p className="mt-6 text-[#A3A3A3] leading-relaxed max-w-md">
                Viale dei Kennedy, Maserà di Padova (PD). Playground all'aperto,
                luci, speakers, tribune. Il cuore pulsante del torneo.
              </p>

              <a
                href="https://www.google.com/maps/search/?api=1&query=Parco+della+Libert%C3%A0+Viale+dei+Kennedy+Maser%C3%A0+di+Padova"
                target="_blank"
                rel="noreferrer"
                data-testid="map-link"
                className="mt-8 inline-flex items-center gap-3 bg-transparent text-white uppercase font-extrabold tracking-[0.15em] px-6 py-3 border-2 border-white hover:bg-white hover:text-black transition-colors duration-300"
              >
                <MapPin size={16} />
                Apri in Google Maps
              </a>
            </div>

            <div className="relative h-72 md:h-80 w-full border-t border-[#2A2A2A]">
              <iframe
                title="Mappa Parco della Libertà"
                src="https://www.google.com/maps?q=Parco+della+Libert%C3%A0+Viale+dei+Kennedy+Maser%C3%A0+di+Padova&output=embed"
                className="absolute inset-0 w-full h-full grayscale contrast-125 brightness-75"
                loading="lazy"
                data-testid="map-iframe"
              />
              <div className="pointer-events-none absolute inset-0 bg-[#050505]/10 mix-blend-multiply" />
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="final-cta"
        className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24"
      >
        <div className="relative border border-[#FF4500] p-10 md:p-16 bg-gradient-to-br from-[#0A0A0A] via-[#050505] to-[#0A0A0A] overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#FF4500]/20 blur-[120px]" />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] font-bold">
                Iscrizioni Aperte
              </p>
              <h2 className="mt-4 font-bebas text-5xl md:text-7xl uppercase leading-[0.9]">
                Pronto a <br />
                rompere <br />
                <span className="text-[#FF4500]">le regole?</span>
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/iscrizioni"
                data-testid="final-cta-iscriviti"
                className="inline-flex items-center justify-center gap-3 bg-[#FF4500] text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-[#FF4500] hover:bg-transparent hover:text-[#FF4500] transition-colors duration-300"
              >
                Iscrivi il team
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/sponsor"
                data-testid="final-cta-sponsor"
                className="inline-flex items-center justify-center gap-3 bg-transparent text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-white hover:bg-white hover:text-black transition-colors duration-300"
              >
                Diventa Sponsor
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
